import SpinnerButton from 'common/components/SpinnerButton';
import { AppSyncFarm } from 'common/models/farm';
import { Nullable, Optional } from 'common/models/optional';
import { FarmUserInfo } from 'common/models/token';
import React from 'react';
import { useKey } from 'react-use';
import { VaultDepositEvent } from 'xstate/vaultDepositEvent';
import {
  vaultDepositService,
  EventUpdateAccountInfoArgs,
  initialDepositContext,
  EventInputChangeArgs,
} from 'xstate/vaultDepositMachine';
import { XStateUtils } from 'xstate/xStateUtils';
import { NumberUtils } from 'utils/numberUtils';
import { CryptoUtils } from 'utils/cryptoUtils';
import { FarmUtils } from 'utils/farmUtils';
// import PercentageSelector from '../PercentageSelector';
import { RemoteEventTxSuccess, RemoteTxEvent } from 'xstate/txMachine';
import { AdminAction, AdminButton } from '../AdminButton';
import debounce from 'lodash/debounce';
import Image from 'common/components/Image';
import { ChainId, tokenSrc } from 'constants/constants';
import { OutboundLink } from 'gatsby-plugin-google-gtag';
import LiquidityInput from '../GeneralInput/LiquidityInput';
import { TokenIcon } from '../TokenIcons/TokenIcon';
import MaxBaseToken from './MaxBaseToken';
import SimpleModal from '../Modal/SimpleModal';
import ModalWithTitleInner from '../Modal/ModalWithTitleInner';
import useSelectedChain from 'hooks/useSelectedChain';
import { useAccount } from 'wagmi';
import { VaultApi } from 'sdk/vaultApi';
import { chains } from 'containers/WalletProvider/WalletProvider';

type Props = {
  isShowing: boolean;
  farm: Nullable<AppSyncFarm>;
  toggle: (event?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  farmUserInfo: Optional<FarmUserInfo>;
  farmUserInfoLoading: boolean;
  refetchFarmUserInfo: () => Promise<void>;
  updatePool: (poolId: number) => Promise<void>;
  type: Optional<string>;
};

const AddLiquidityModalContainer: React.FC<Props> = (props) => {
  const { chainId } = useSelectedChain();
  const { address, isConnected } = useAccount();
  const chain = chains.find((c) => c.id === chainId);

  const [selectedToken, setSelectedToken] = React.useState('eth');

  // const [isPercentageSelectorDisabled, setIsPercentageSelectorDisabled] = React.useState(false);

  const [amount, setAmount] = React.useState(initialDepositContext.amount);
  const [price, setPrice] = React.useState(initialDepositContext.tokenPrice);
  const [tokenBalance, setTokenBalance] = React.useState(0n);
  const [tokenBalanceUsd, setTokenBalanceUsd] = React.useState(
    initialDepositContext.availableTokenUsd
  );
  const [tokenMaxDeposit, setTokenMaxDeposit] = React.useState(
    initialDepositContext.tokenMaxDeposit
  );
  const [inputError, setInputError] = React.useState(
    initialDepositContext.inputError
  );
  const [isLoadingData, setIsLoadingData] = React.useState(
    initialDepositContext.isLoadingData
  );
  const [isDepositing, setIsDepositing] = React.useState(
    initialDepositContext.isDepositing
  );
  const [isTokenApproved, setIsTokenApproved] = React.useState<
    Nullable<boolean>
  >(initialDepositContext.isTokenApproved);
  const [isApprovingToken, setIsApprovingToken] = React.useState(
    initialDepositContext.isApprovingToken
  );
  const [storedTx, setStoredTx] = React.useState(
    initialDepositContext.storedTx
  );

  const [step, setStep] = React.useState(1);

  const baseToken = FarmUtils.getBaseToken(props.farm);

  const isTokenEth =
    selectedToken === 'eth' &&
    baseToken?.displayName === 'wETH' &&
    props.farm?.chainId !== ChainId.Polygon &&
    props.farm?.chainId !== ChainId.Celo;
  // const [innerLiquidityPercentage, setInnerLiquidityPercentage] = isTokenEth ? React.useState(0) : React.useState(
  //   initialDepositContext.liquidityPercentage
  // );
  const baseTokenDisplayName = isTokenEth ? 'ETH' : baseToken?.displayName;

  const refreshData = async () => {
    if (props.farm && chainId) {
      await props.updatePool(props.farm.poolId);
      await props.refetchFarmUserInfo();
    }
  };
  const debouncedRefreshData = React.useMemo(
    () => debounce(refreshData, 750),
    [chainId, props.farm]
  );

  const isEthVault =
    props.farm?.displayName === 'wETH Vault' &&
    props.farm?.chainId !== ChainId.Polygon &&
    props.farm?.chainId !== ChainId.Celo;

  const toggleToken = (event: React.MouseEvent) => {
    // setIsPercentageSelectorDisabled(false);
    if (event.currentTarget.textContent?.toLocaleLowerCase() === 'weth') {
      // setInnerLiquidityPercentage(0);
      setAmount('0');
      setSelectedToken('weth');
    } else {
      // setInnerLiquidityPercentage(0);
      setAmount('0');
      setSelectedToken('eth');
    }
  };

  React.useEffect(() => {
    const subscription = vaultDepositService.subscribe((state) => {
      if (state.context.tokenPrice !== price) {
        setPrice(state.context.tokenPrice);
      }

      if (state.context.availableToken !== tokenBalance) {
        setTokenBalance(state.context.availableToken);
      }

      if (state.context.availableTokenUsd !== tokenBalanceUsd) {
        setTokenBalanceUsd(state.context.availableTokenUsd);
      }

      if (state.context.tokenMaxDeposit !== tokenMaxDeposit) {
        setTokenMaxDeposit(state.context.tokenMaxDeposit ?? 0n);
      }

      if (state.context.inputError != inputError) {
        setInputError(state.context.inputError);
      }

      if (state.context.isLoadingData !== isLoadingData) {
        setIsLoadingData(state.context.isLoadingData);
      }

      if (state.context.isDepositing !== isDepositing) {
        setIsDepositing(state.context.isDepositing);
      }

      if (state.context.isTokenApproved !== isTokenApproved) {
        setIsTokenApproved(state.context.isTokenApproved);
      }

      if (state.context.isApprovingToken !== isApprovingToken) {
        setIsApprovingToken(state.context.isApprovingToken);
      }

      if (state.context.storedTx !== storedTx) {
        setStoredTx(state.context.storedTx);
      }
    });

    vaultDepositService.onEvent((e) => {
      if (e.type === RemoteTxEvent.REMOTE_TX_SUCCESS) {
        const successEventValue = (e as RemoteEventTxSuccess).value
          .functionName;
        if (successEventValue && successEventValue === 'vaultDeposit') {
          setStep(3);
        }
        if (
          successEventValue &&
          successEventValue === 'approveTokenAllowance'
        ) {
          setStep(2);
        }
        if (successEventValue && successEventValue === 'resetTokenAllowance') {
          setStep(1);
        }
        void debouncedRefreshData();
      }
    });

    return subscription.unsubscribe;
    // Note that if isDepositModalOpen, isWithdrawModalOpen are not included in the array here
    // Then the function will not re-render
  }, [
    vaultDepositService,
    amount,
    price,
    storedTx,
    inputError,
    tokenBalance,
    tokenBalanceUsd,
    tokenMaxDeposit,
    isLoadingData,
    isDepositing,
    isTokenApproved,
    isApprovingToken,
    // isPercentageSelectorDisabled
  ]);

  useKey('Escape', () => props.toggle());

  // disable Percentage Selector on amount change
  // React.useEffect(() => {
  //   if (!isPercentageSelectorDisabled){
  //     const args: EventLiquidityPercentageChangeArgs = {
  //       value: innerLiquidityPercentage
  //     };
  //     vaultDepositService.send(VaultDepositEvent.LIQUIDITY_PERCENTAGE_CHANGE, XStateUtils.createEventPayload(args));
  //   }
  // }, [innerLiquidityPercentage, isPercentageSelectorDisabled]);

  // React.useEffect(() => {
  //   if (isPercentageSelectorDisabled){
  //     const args: EventInputChangeArgs = {
  //       amount: amount
  //     };
  //     vaultDepositService.send(VaultDepositEvent.INPUT_CHANGE, XStateUtils.createEventPayload(args));
  //   }
  // }, [amount, isPercentageSelectorDisabled]);

  React.useEffect(() => {
    const args: EventInputChangeArgs = {
      amount: amount,
      approveExactAmount: false,
    };
    vaultDepositService.send(
      VaultDepositEvent.INPUT_CHANGE,
      XStateUtils.createEventPayload(args)
    );
  }, [amount]);

  React.useEffect(() => {
    if (
      VaultApi.Vault.getInstance() != null &&
      isConnected &&
      address &&
      chainId &&
      props.farm
    ) {
      const payload: EventUpdateAccountInfoArgs = {
        address: address,
        chainId: chainId,
        farm: props.farm,
        token: selectedToken,
      };
      vaultDepositService.send(
        VaultDepositEvent.UPDATE_ACCOUNT_INFO,
        XStateUtils.createEventPayload(payload)
      );
    }
  }, [chainId, address, isConnected, props.farm, selectedToken]);

  const isDepositButtonDisabled =
    !amount ||
    amount === '0' ||
    isDepositing ||
    inputError != null ||
    isLoadingData;

  const adminActions: AdminAction[] = [
    {
      text: 'Reset Token Allowance',
      onClick: () =>
        vaultDepositService.send(VaultDepositEvent.RESET_TOKEN_ALLOWANCE),
    },
  ];

  return (
    <SimpleModal isShowing={props.isShowing}>
      {step === 1 && (
        <ModalWithTitleInner title="Step 1: Add liquidity and approve">
          <div className="flex flex-col">
            <div className="grid">
              <div className="text-center primary-text-color m-7 font-medium">
                Enter the amount of {baseTokenDisplayName} you want to deposit.
                <br />
                Don't have {baseTokenDisplayName} token?{' '}
                <OutboundLink
                  href={props.farm?.extras.tradeUrl as string}
                  className="primary-color"
                >
                  Trade
                </OutboundLink>
              </div>
              {isEthVault && (
                <div className="flex flex-row justify-around items-center w-full mb-3">
                  <button
                    onClick={toggleToken}
                    className={
                      selectedToken === 'eth'
                        ? 'selected-vault-button primary-text-color font-semibold'
                        : 'vault-button primary-text-color'
                    }
                  >
                    <Image
                      src={`${tokenSrc}logo_256_weth.svg?2`}
                      alt="ETH icon"
                      height="30px"
                      width="30px"
                      marginRight="10px"
                    />
                    ETH
                  </button>
                  <button
                    onClick={toggleToken}
                    className={
                      selectedToken === 'weth'
                        ? 'selected-vault-button primary-text-color font-semibold'
                        : 'vault-button primary-text-color'
                    }
                  >
                    <Image
                      src={`${tokenSrc}logo_256_weth.svg?2`}
                      alt="wETH icon"
                      height="30px"
                      width="30px"
                      marginRight="10px"
                    />
                    wETH
                  </button>
                </div>
              )}
              <div className="flex flex-col content-inner-bg-color rounded-[20px] p-5">
                <LiquidityInput
                  selectedToken={baseToken.name}
                  isNumber={true}
                  label="Enter Amount"
                  step="1"
                  value={amount}
                  onChange={setAmount}
                  placeholder="Enter Amount"
                  token={baseToken}
                  tokenBalance={tokenBalance}
                />
                <MaxBaseToken
                  farm={props.farm}
                  availableToken={tokenBalance}
                  availableTokenUSD={Number(tokenBalanceUsd)}
                  tokenPrice={baseToken.price}
                  isLoading={isLoadingData}
                  isDepositing={isDepositing}
                  amount={amount}
                  tokenDisplayName={baseTokenDisplayName}
                  onChange={setAmount}
                />
              </div>
              {inputError != null && <div className="error">{inputError}</div>}
              {props.farm &&
                tokenMaxDeposit.toString() !== CryptoUtils.MAX_NUMBER && (
                  <div className="mt-3 warning-text-color text-sm font-semibold w-full text-center">
                    {`Max ${baseTokenDisplayName} Deposit
                  ${NumberUtils.BNtoNumberWithoutDecimals(
                    tokenMaxDeposit.toString(),
                    baseToken?.decimals || 18
                  )}`}
                  </div>
                )}
            </div>
          </div>
          <div className="flex justify-center gap-3 items-center h-24 box-footer pb-3">
            <button
              onClick={props.toggle}
              className="primary-text-color border-[1px] border-gray-200 font-semibold"
            >
              Cancel
            </button>
            {isTokenApproved === false ? (
              <SpinnerButton
                className="primary-button-bg-color"
                isLoading={isApprovingToken || isLoadingData}
                disabled={isDepositButtonDisabled}
                onClick={() =>
                  vaultDepositService.send(
                    VaultDepositEvent.APPROVE_TOKEN_ALLOWANCE
                  )
                }
                data-dd-action-name={`Approve ${baseTokenDisplayName}`}
              >
                Approve
              </SpinnerButton>
            ) : (
              <SpinnerButton
                className="primary-button-bg-color"
                isLoading={isDepositing || isLoadingData}
                disabled={isDepositButtonDisabled}
                onClick={() => setStep(2)}
                data-dd-action-name={`Deposit ${baseTokenDisplayName}`}
              >
                Continue
              </SpinnerButton>
            )}
            {chain?.testnet && <AdminButton actions={adminActions} />}
          </div>
        </ModalWithTitleInner>
      )}

      {/* ********** STEP 2 ********** */}
      {step === 2 && (
        <ModalWithTitleInner title="Step 2: Confirm and Earn">
          <div className="flex flex-col">
            <div className="grid">
              <div className="text-center primary-text-color m-7 font-medium">
                Confirm your deposit and start earning
              </div>
              <div className="text-lg tablet:text-3xl font-semibold flex flex-row gap-3 justify-center items-center">
                {amount}
                <TokenIcon name={baseToken.name ?? ''} width="40px" />
                {baseTokenDisplayName}
              </div>
            </div>
          </div>
          <div className="flex justify-center gap-3 items-center h-24 box-footer  pb-3">
            <button
              onClick={() => setStep(1)}
              className="primary-text-color border-[1px] border-gray-200 font-semibold"
            >
              Previous
            </button>
            {isTokenApproved === false ? (
              <SpinnerButton
                className="primary-button-bg-color"
                isLoading={isApprovingToken || isLoadingData}
                disabled={isDepositButtonDisabled}
                onClick={() =>
                  vaultDepositService.send(
                    VaultDepositEvent.APPROVE_TOKEN_ALLOWANCE
                  )
                }
                data-dd-action-name={`Approve ${baseTokenDisplayName}`}
              >
                Approve
              </SpinnerButton>
            ) : (
              <SpinnerButton
                className="primary-button-bg-color"
                isLoading={isDepositing || isLoadingData}
                disabled={isDepositButtonDisabled}
                onClick={() => {
                  vaultDepositService.send(VaultDepositEvent.DEPOSIT);
                }}
                data-dd-action-name={`Deposit ${baseTokenDisplayName}`}
              >
                Confirm!
              </SpinnerButton>
            )}
            {chain?.testnet && <AdminButton actions={adminActions} />}
          </div>
        </ModalWithTitleInner>
      )}

      {/* ********** STEP 3 ********** */}
      {step === 3 && (
        <ModalWithTitleInner title="Congratulation!">
          <div className="flex flex-col">
            <div className="flex flex-col items-center">
              <Image
                src="https://ichi-images.s3.amazonaws.com/misc/confetti.svg"
                marginTop="40px"
                alt="Congratulations!"
              />
              <div className="text-lg text-center mt-10 px-14">
                {'You have deposited '}
                <strong>{`${storedTx?.value} `}</strong>
                <strong>{baseTokenDisplayName}</strong>
                {' and have started earning.'}
              </div>
            </div>
          </div>
          <div className="flex justify-center gap-3 items-center h-24 box-footer  pb-3">
            <button
              onClick={props.toggle}
              className="primary-text-color border-[1px] border-gray-200 font-semibold"
            >
              Close
            </button>
          </div>
        </ModalWithTitleInner>
      )}

      {/* ********** STEP 4 ********** */}
      {step === 4 && (
        <ModalWithTitleInner title="Share on Twitter">
          <div className="flex flex-col items-start content-inner-bg-color px-2 tablet:px-6 py-6 rounded-2xl">
            <div className="flex justify-start items-center gap-3 text-xl font-medium">
              <Image
                src="https://ichi-images.s3.amazonaws.com/misc/twitter.svg"
                alt="Twitter"
              />
              Share this with your followers
            </div>
            <div className="text-lg text-left my-4 styled-content px-2 tablet:px-4 py-4 rounded-[10px]">
              I deposited to the @ichifoundation {props.farm?.displayName} on
              app.ichi.org and am now earning IRR. #ICHI makes earning in #DeFi
              easy.
            </div>
            <div className="text-right w-full">
              <a className="twitter-share-button">Tweet</a>
            </div>
          </div>
          <div className="flex justify-center gap-3 items-center h-24 box-footer  pb-3">
            <button
              onClick={props.toggle}
              className="primary-text-color border-[1px] border-gray-200 font-semibold"
            >
              Close
            </button>
          </div>
        </ModalWithTitleInner>
      )}
    </SimpleModal>
  );
};

export default AddLiquidityModalContainer;
